import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, forkJoin, map, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { backendEndPointToken } from '../app.config';
import { SessionService } from './auth/session.service';
import { DisableCountParam, LoadingUIEnabled } from '../loading.interceptor';
import { Bundle } from 'fhir/r4';

export interface SetupTask {
  id: string;
  title: string;
  description: string;
  icon: string;
  link: string;
  isCompleted: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OnboardingSetupService {
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly sessionService = inject(SessionService);

  private tasksSubject = new BehaviorSubject<SetupTask[]>([]);
  public tasks$ = this.tasksSubject.asObservable();

  public get pendingTasksCount$(): Observable<number> {
    return this.tasks$.pipe(
      map(tasks => tasks.filter(t => !t.isCompleted).length)
    );
  }

  public get isFullySetup$(): Observable<boolean> {
    return this.tasks$.pipe(
      map(tasks => tasks.length > 0 && tasks.every(t => t.isCompleted))
    );
  }

  public refreshTasks(): void {
    const session = this.sessionService.getSession();
    if (!session || !session.isLoggedIn) {
      this.tasksSubject.next([]);
      return;
    }

    const role = (session.role || '').toLowerCase();
    const ctx = new HttpContext().set(LoadingUIEnabled, false).set(DisableCountParam, true);

    if (role === 'hospitaladmin') {
      this.checkHospitalAdminTasks(ctx);
    } else if (role === 'healthcareadmin') {
      this.checkHealthcareAdminTasks(ctx);
    } else {
      // Other roles have no setup tasks for now
      this.tasksSubject.next([]);
    }
  }

  private checkHospitalAdminTasks(ctx: HttpContext): void {
    const reqs = {
      services: this.checkResourceExists('ActivityDefinition?status=active&_count=1', ctx),
      wards: this.checkResourceExists('Location?_count=1', ctx),
      supplyStock: this.checkResourceExists('SupplyDelivery?_count=1', ctx),
      medicineStock: this.checkResourceExists('Medication?_count=1', ctx),
      practitioners: this.checkResourceCountGreaterThan('Practitioner?_count=2', 1, ctx)
    };

    forkJoin(reqs).subscribe(results => {
      const tasks: SetupTask[] = [
        {
          id: 'hospital-practitioners',
          title: 'Register Staff',
          description: 'Add doctors, nurses, and other practitioners to your hospital.',
          icon: 'badge',
          link: '/app/practitioners',
          isCompleted: results.practitioners
        },
        {
          id: 'hospital-services',
          title: 'Configure Services',
          description: 'Set up the medical services and procedures offered by your hospital.',
          icon: 'local_hospital',
          link: '/app/services-details',
          isCompleted: results.services
        },
        {
          id: 'hospital-wards',
          title: 'Add Admission Wards',
          description: 'Define the locations and beds available for patient admissions.',
          icon: 'hotel',
          link: '/app/admission-location',
          isCompleted: results.wards
        },
        {
          id: 'hospital-supply',
          title: 'Add Supply Stock',
          description: 'Add essential medical supplies to your inventory.',
          icon: 'inventory_2',
          link: '/app/supply-stock',
          isCompleted: results.supplyStock
        },
        {
          id: 'hospital-medicine',
          title: 'Add Medicine Stock',
          description: 'Register pharmacy products and medications in your database.',
          icon: 'medication',
          link: '/app/medicine-stock',
          isCompleted: results.medicineStock
        }
      ];
      this.tasksSubject.next(tasks);
    });
  }

  private checkHealthcareAdminTasks(ctx: HttpContext): void {
    const reqs = {
      patients: this.checkResourceExists('Patient?_count=1', ctx)
    };

    forkJoin(reqs).subscribe(results => {
      const tasks: SetupTask[] = [
        {
          id: 'admin-patients',
          title: 'Register Patients',
          description: 'Register the first patients in the system to get started.',
          icon: 'people',
          link: '/app/patient-registration/pending',
          isCompleted: results.patients
        }
      ];
      this.tasksSubject.next(tasks);
    });
  }

  private checkResourceExists(query: string, ctx: HttpContext): Observable<boolean> {
    return this.http.get<Bundle>(`${this.backendUrl}/${query}`, { context: ctx }).pipe(
      map(bundle => (bundle.total !== undefined && bundle.total > 0) || (bundle.entry && bundle.entry.length > 0) ? true : false),
      catchError(() => of(false))
    );
  }

  private checkResourceCountGreaterThan(query: string, count: number, ctx: HttpContext): Observable<boolean> {
    return this.http.get<Bundle>(`${this.backendUrl}/${query}`, { context: ctx }).pipe(
      map(bundle => (bundle.total !== undefined && bundle.total > count) || (bundle.entry && bundle.entry.length > count) ? true : false),
      catchError(() => of(false))
    );
  }
}
